// planList.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Divider,
} from '@mui/material';
import subscriptionService from '../../../services/subscriptionService';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default function PlanList({ orderInfo = { plans: [] }, setOrderInfo }) {
  const [bestValue, setBestValue] = useState('0.00');
  const plans = orderInfo.plans || [];
  const selectedValue = orderInfo.pricing ?? '';

  const changePricing = (_hold, _field, newPricing, plan) => {
    const chosen = plan ?? plans.find((p) => String(p.value) === String(newPricing));

    if (!chosen) return;

    setOrderInfo((prev) => ({
      ...prev,
      amountDue: chosen.details.price,
      includeTrial: chosen.details.include_trial,
      pricing: newPricing,
      selectedPlan: chosen,
    }));
  };

  useEffect(() => {
    async function setup() {
      let fetched = await subscriptionService.fetchAvailablePlans();

      // Optionally insert "Free" at the top
      if (orderInfo.includeFree) {
        fetched.unshift({
          label: 'Free',
          value: '0',
          details: {
            frequency: 'Free',
            price: '0.00',
            user_level: '',
          },
        });
      }

      // Sort by price DESC
      fetched = fetched.slice().sort((a, b) => {
        const priceA = parseFloat(a.details.price);
        const priceB = parseFloat(b.details.price);
        return priceB - priceA;
      });

      await computeBestValueAndPrimeSelection(fetched);
    }

    async function computeBestValueAndPrimeSelection(getPlans) {
      if (!getPlans || getPlans.length === 0) return;

      const yearlyPlan = getPlans.find((p) => p.details.frequency === 'Yearly');

      // Save plans to orderInfo
      setOrderInfo((prev) => ({ ...prev, plans: getPlans }));

      if (!yearlyPlan) return;

      // Auto-select Yearly by default (matches original behavior)
      changePricing(null, null, yearlyPlan.value, yearlyPlan);

      // Compute "Best Value" vs the smallest monthly plan (ignore Free if present)
      const last = getPlans[getPlans.length - 1];
      const smallest = last?.label === 'Free' ? getPlans[getPlans.length - 2] : last;

      if (!smallest) return;

      const yearlyPrice = parseFloat(yearlyPlan.details.price);
      const monthlyPrice = parseFloat(smallest.details.price);
      let savings = yearlyPrice - monthlyPrice * 12; // negative = savings
      savings = (savings * -1).toFixed(2);

      setBestValue(savings);
    }

    if (!plans || plans.length === 0) {
      setup();
    } else {
      // Plans already present; recompute best value
      (async () => {
        const yearlyPlan = plans.find((p) => p.details.frequency === 'Yearly');
        if (!yearlyPlan) return;

        const last = plans[plans.length - 1];
        const smallest = last?.label === 'Free' ? plans[plans.length - 2] : last;

        if (smallest) {
          const yearlyPrice = parseFloat(yearlyPlan.details.price);
          const monthlyPrice = parseFloat(smallest.details.price);
          let savings = yearlyPrice - monthlyPrice * 12;
          savings = (savings * -1).toFixed(2);
          setBestValue(savings);
        }
      })();
    }
  }, []);

  const handleRowClick = (value, plan) => {
    changePricing(null, null, value, plan);
  };

  return (
    <FormControl fullWidth>
      <RadioGroup
        value={selectedValue}
        onChange={(e) => {
          const value = e.target.value;
          const plan = plans.find((p) => String(p.value) === String(value));
          changePricing(null, null, value, plan);
        }}
      >
        {plans.map((plan, idx) => {
          const isActive = String(selectedValue) === String(plan.value);
          const isYearly = plan.details.frequency === 'Yearly';
          const isStaffOnly = plan.details.staff_only;
          const priceNum = parseFloat(plan.details.price || '0');
          const perMonth =
            isYearly && priceNum
              ? (priceNum / 12).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                })
              : null;

          const userLevel =
            (plan.details.user_level || '')
              .replace(/^\s+|\s+$/g, '')
              .replace(/^./, (m) => m.toUpperCase()) || '';

          return (
            <Paper
              key={plan.value ?? idx}
              variant="outlined"
              onClick={() => handleRowClick(plan.value, plan)}
              sx={{
                p: 1.25,
                mb: 1.25,
                borderRadius: 2,
                borderWidth: 2,
                cursor: 'pointer',
                borderColor: isActive ? '#7A64EB' : 'divider',
                '&:hover': { borderColor: isActive ? '#7A64EB' : 'text.disabled' },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ width: '100%' }}>
                {/* Big radio, no ripple */}
                <FormControlLabel
                  value={plan.value}
                  control={<Radio disableRipple sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }} />}
                  label=""
                  sx={{ m: 0 }}
                />

                {/* Text content */}
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {plan.label}{' '}
                      {userLevel ? (
                        <Typography component="span" variant="subtitle2" color="text.secondary">
                          {userLevel}
                        </Typography>
                      ) : null}
                    </Typography>

                    {/* Badges */}
                    {isStaffOnly ? (
                      <Chip size="small" label="STAFF ONLY" color="error" sx={{ color: '#fff' }} />
                    ) : isYearly ? (
                      <Chip
                        size="small"
                        label={`Best Value: Save $${bestValue}`}
                        sx={{ bgcolor: '#E35640', color: '#fff' }}
                      />
                    ) : null}
                  </Stack>

                  {/* Price line */}
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {currency.format(priceNum)}
                    </Typography>
                    {perMonth && (
                      <Typography variant="caption" color="text.secondary">
                        ({perMonth}/month)
                      </Typography>
                    )}
                  </Stack>

                  {/* Trial note */}
                  {plan.details.include_trial ? (
                    <Typography variant="caption" color="text.secondary">
                      Includes 1 month trial
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </RadioGroup>
    </FormControl>
  );
}
